import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import {
  bumpAttemptAfterSubmit,
  getAuthBaseUserId,
  isMultiplaySession,
  syncAttemptFromSubmittedId,
} from "../../utils/multiplay";
import { addReportToServer } from "../../functions/sendReport";
import "./game.css";

const FIXED_GRID_SIZE = 15;

function maskWord(word) {
  return word
    .split("")
    .map((char, i) => {
      if (i % 3 === 0) return char;
      const nextIsUnderscore = (i + 1) % 3 !== 0;
      return nextIsUnderscore ? "_ " : "_";
    })
    .join("");
}

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Resolve display name for spotSubmit / DB (guests often have empty firstName/lastName). */
function resolveSubmitUserName(user) {
  if (!user) return "";
  const raw = String(user.name ?? "").trim();
  if (raw && raw !== "null" && raw !== "null null") return raw;

  const email = String(user.email ?? "").trim();
  if (email.includes("@")) {
    const local = email.split("@")[0].trim();
    if (local) return local;
  }

  const employeeId = String(user.employeeId ?? "").trim();
  if (employeeId) return employeeId;

  return String(user.userId ?? "").trim();
}

/** Up to two lines: honor <br>, else split at first comma, else split words in half. */
function splitTitleTwoLines(raw) {
  const t = String(raw || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .trim();
  if (!t) return [];

  if (t.includes("\n")) {
    return t
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 2);
  }

  const comma = t.indexOf(",");
  if (comma > 0) {
    return [t.slice(0, comma + 1).trim(), t.slice(comma + 1).trim()].filter(Boolean);
  }

  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return words;
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

function normalizeRules(val) {
  if (Array.isArray(val)) {
    return val.map((r) => String(r).trim()).filter(Boolean);
  }
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        return parsed.map((r) => String(r).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
    const regex = /s:\d+:"([^"]*)";/g;
    const out = [];
    let m;
    while ((m = regex.exec(val)) !== null) {
      if (m[1].trim()) out.push(m[1].trim());
    }
    if (out.length) return out;
  }
  return [];
}

function canPlace(grid, word, r, c, dx, dy) {
  for (let i = 0; i < word.length; i++) {
    const nr = r + dx * i;
    const nc = c + dy * i;
    if (
      nr < 0 || nc < 0 ||
      nr >= grid.length || nc >= grid.length ||
      (grid[nr][nc] && grid[nr][nc] !== word[i])
    ) return false;
  }
  return true;
}

function placeWords(grid, wordList, directions) {
  wordList.forEach((word) => {
    let placed = false;
    let attempts = 0;
    while (!placed && attempts < 5000) {
      const [dx, dy] = directions[Math.floor(Math.random() * directions.length)];
      const r = Math.floor(Math.random() * grid.length);
      const c = Math.floor(Math.random() * grid.length);
      if (canPlace(grid, word, r, c, dx, dy)) {
        for (let i = 0; i < word.length; i++) {
          grid[r + dx * i][c + dy * i] = word[i];
        }
        placed = true;
      }
      attempts++;
    }
  });
}

function fillRandom(grid) {
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid.length; c++) {
      if (!grid[r][c]) {
        grid[r][c] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
      }
    }
  }
}

export default function Game() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [grid, setGrid] = useState([]);
  const [selected, setSelected] = useState([]);
  const [foundCells, setFoundCells] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(0);
  const [statusChecked, setStatusChecked] = useState(false);
  const [tooltipIndex, setTooltipIndex] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const isDragging = useRef(false);
  const directionRef = useRef(null);
  const gridInitialized = useRef(false);
  const submittingRef = useRef(false);
  const tooltipRef = useRef(null);
  const mobileHintRef = useRef(null);
  const reportIdRef = useRef(null);

  console.log("user", user);

  const isDemo = user?.role === "demobypass";

  const resolvedSubmitUserName = useMemo(() => resolveSubmitUserName(user), [user]);
  const DEMO_WORD_LIMIT = 2;

  const toBool = (v) => v === true || v === "true";

  const DIRECTIONS = useMemo(() => {
    if (!themeData) return [[0, 1], [1, 0]];
    const dirs = [[0, 1], [1, 0]];
    if (toBool(themeData.rightDiagonal)) dirs.push([1, 1]);
    if (toBool(themeData.leftDiagonal)) dirs.push([1, -1]);
    return dirs;
  }, [themeData]);

  const words = useMemo(() => {
    if (!themeData?.words) return [];
    const raw = Array.isArray(themeData.words) ? themeData.words : [];
    return raw.map((w) => String(w).toUpperCase());
  }, [themeData]);

  const wordIndications = useMemo(() => {
    if (!themeData?.wordpuzzle_words_indication) return [];
    return Array.isArray(themeData.wordpuzzle_words_indication)
      ? themeData.wordpuzzle_words_indication
      : [];
  }, [themeData]);
  const rulesList = useMemo(() => normalizeRules(themeData?.rules), [themeData]);

  const POINTS_PER_WORD = Number(themeData?.points || 0);
  const submitLimit = Number(themeData?.submitShow || 0);
  const wordDisplayMode = (themeData?.word_display_mode || "normal").toLowerCase();

  const textColor = themeData?.text_color || "#111";
  const titleTextColor =
    themeData?.landing_page_title_color || themeData?.text_color || "#111";
  const btnTextColor = themeData?.button_color || themeData?.landing_page_title_color || "#ffffff";
  const btnBgColor = themeData?.button_backgroundcolor || themeData?.btn_bgcolor || "#000000";
  const gridColor = themeData?.grid_color || "#2196f3";
  const beginSubmitProgressBgColor = themeData?.begin_submit_progress_bg_color || btnBgColor;
  const gridActiveTextColor = themeData?.grid_active_text_color || "#ffffff";
  const wordButtonTextColor = themeData?.word_button_text_color || btnTextColor;
  const wordButtonBgColor = themeData?.word_button_bg_color || btnBgColor;
  const wordGridBgColor = themeData?.word_grid_bg_color || "#ffffff";
  const wordGridTextColor = themeData?.word_grid_text_color || "#000000";

  const gameTitleLines = useMemo(() => {
    if (!themeData?.landing_page_title) return [];
    return splitTitleTwoLines(themeData.landing_page_title);
  }, [themeData]);

  const totalWords = words.length;
  const foundCount = foundWords.length;
  const progressPercent = totalWords ? Math.round((foundCount / totalWords) * 100) : 0;
  const canShowSubmit = submitLimit > 0 ? foundCount >= submitLimit : foundCount === totalWords;

  useEffect(() => {
    reportIdRef.current = user?.reportId ?? null;
  }, [user]);

  useEffect(() => {
    if (!user || !adminToken) return;
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [dispatch, user, adminToken, themeStatus]);

  useEffect(() => {
    if (!user) return;
    if (themeStatus !== "succeeded" || !themeData) return;
    if (!adminToken) {
      setStatusChecked(true);
      return;
    }
    if (isMultiplaySession(user)) {
      setStatusChecked(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: getAuthBaseUserId(user),
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            isMultiplaySession: user.isMultiplaySession,
          }),
        });
        const data = await resp.json();
        if (cancelled) return;
        if (data.success && data.completed) {
          navigate("/thankyou", { replace: true });
          return;
        }
      } catch (e) {
        console.error("spotGameStatus", e);
      }
      setStatusChecked(true);
    })();
    return () => { cancelled = true; };
  }, [user, adminToken, themeStatus, themeData, navigate]);

  useEffect(() => {
    if (!words.length || !themeData || gridInitialized.current) return;
    const size = Number(themeData.gridsize) || FIXED_GRID_SIZE;
    const empty = Array(size).fill(null).map(() => Array(size).fill(""));
    placeWords(empty, words, DIRECTIONS);
    fillRandom(empty);
    setGrid(empty);
    gridInitialized.current = true;
  }, [words, themeData, DIRECTIONS]);

  useEffect(() => {
    if (!statusChecked) return;
    const interval = setInterval(() => setTime((p) => p + 1), 1000);
    return () => clearInterval(interval);
  }, [statusChecked]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest(".info-icon")) return;
      const inDesktopTooltip = tooltipRef.current?.contains(e.target);
      const inMobilePanel = mobileHintRef.current?.contains(e.target);
      if (!inDesktopTooltip && !inMobilePanel) setTooltipIndex(null);
    };
    if (tooltipIndex !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [tooltipIndex]);

  const sameCell = (a, b) => a[0] === b[0] && a[1] === b[1];

  const start = useCallback((r, c) => {
    isDragging.current = true;
    directionRef.current = null;
    setSelected([[r, c]]);
  }, []);

  const move = useCallback((r, c) => {
    if (!isDragging.current) return;
    setSelected((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const secondLast = prev[prev.length - 2];

      if (secondLast && sameCell([r, c], secondLast)) {
        if (prev.length === 2) directionRef.current = null;
        return prev.slice(0, -1);
      }
      if (prev.some((p) => sameCell(p, [r, c]))) return prev;

      const dr = r - last[0];
      const dc = c - last[1];
      if (Math.abs(dr) > 1 || Math.abs(dc) > 1) return prev;

      if (!directionRef.current && prev.length === 1) {
        directionRef.current = [dr, dc];
      }
      if (directionRef.current) {
        const [dirR, dirC] = directionRef.current;
        if (Math.sign(dr) !== Math.sign(dirR) || Math.sign(dc) !== Math.sign(dirC)) {
          return prev;
        }
      }
      return [...prev, [r, c]];
    });
  }, []);

  const checkWord = useCallback(() => {
    setSelected((currentSelected) => {
      if (currentSelected.length === 0) return currentSelected;

      const letters = currentSelected.map(([r, c]) => grid[r][c]).join("");
      const reverse = letters.split("").reverse().join("");
      const match = words.find((w) => w === letters || w === reverse);

      if (match && !foundWords.includes(match)) {
        if (isDemo && foundWords.length >= DEMO_WORD_LIMIT) return [];

        const updatedFound = [...foundWords, match];
        setFoundWords(updatedFound);
        setScore((p) => p + POINTS_PER_WORD);
        setFoundCells((prev) => [...prev, ...currentSelected]);

        if (isDemo && updatedFound.length === DEMO_WORD_LIMIT) {
          Swal.fire({
            icon: "success",
            title: "Thank you for playing.",
            text: "Subscribe to any PLAN to play with your peers.",
            allowOutsideClick: false,
          }).then(() => {
            window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
          });
        }
      }
      return [];
    });
  }, [grid, words, foundWords, POINTS_PER_WORD, isDemo]);

  const end = useCallback(() => {
    isDragging.current = false;
    directionRef.current = null;
    checkWord();
  }, [checkWord]);

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    if (!user || !adminToken || !themeData) return;
    submittingRef.current = true;

    const timeStr = formatTime(time);
    const scoreVal = score;
    const submitUserId = getAuthBaseUserId(user);

    try {
      const resp = await fetch(apiUrl("/spotSubmit"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: submitUserId,
          email: user.email || "",
          name: resolvedSubmitUserName,
          time: timeStr,
          wordpuzzle_words: JSON.stringify(foundWords),
          spot_score: scoreVal,
          total_score: scoreVal,
          currentTheme: themeData.themename || themeData.themeName,
          isMultiplaySession: isMultiplaySession(user),
        }),
      });

      const text = await resp.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }

      if (!resp.ok) {
        const msg = payload?.error || text || "Submit failed";
        await Swal.fire({ icon: "error", title: "Submit failed", text: msg });
        submittingRef.current = false;
        return;
      }

      const isDemoResponse = payload?.demo === true || (typeof text === "string" && text.trim() === "demo");

      if (isDemoResponse) {
        const msg = payload?.uniqueMsg || `Your score is ${scoreVal} (demo).`;
        sessionStorage.setItem("spotThankYouMsg", msg);
        await Swal.fire({
          icon: "success",
          title: "Thank you for playing.",
          text: "Subscribe to any PLAN to play with your peers.",
        });
        window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
        return;
      }

      if (payload?.submittedUserId) {
        syncAttemptFromSubmittedId(user, payload.submittedUserId);
      } else {
        bumpAttemptAfterSubmit(user);
      }

      await addReportToServer(user, {
        points: scoreVal,
        elapsedSeconds: time,
        answers: foundWords,
        reportIdRef,
      });

      const uniqueMsg = payload?.uniqueMsg || "";
      if (uniqueMsg) sessionStorage.setItem("spotThankYouMsg", uniqueMsg);
      navigate("/leaderboard");
    } catch (e) {
      console.error("spotSubmit", e);
      await Swal.fire({ icon: "error", title: "Network error", text: String(e.message) });
    }
    submittingRef.current = false;
  }, [user, adminToken, themeData, time, score, foundWords, navigate, resolvedSubmitUserName]);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el?.dataset?.pos) {
      const [rr, cc] = el.dataset.pos.split(",");
      move(+rr, +cc);
    }
  }, [move]);

  const toggleTooltip = useCallback((index) => {
    setTooltipIndex((prev) => (prev === index ? null : index));
  }, []);

  useEffect(() => {
    if (!rulesOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setRulesOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [rulesOpen]);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  if (themeStatus !== "succeeded" || !themeData || !statusChecked) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  const gridSize = grid.length || FIXED_GRID_SIZE;
  const desktopCellSize = Math.max(16, Math.floor(376 / gridSize));
  const mobileCellSize = Math.max(12, Math.floor(270 / gridSize));
  const desktopFontSize = Math.max(10, Math.floor(desktopCellSize * 0.56));
  const mobileFontSize = Math.max(8, Math.floor(mobileCellSize * 0.56));
  const leftWordCount = Math.ceil(words.length / 2);

  const renderInfoIcon = (index, showInlineTooltip = true) => {
    const indication = wordIndications[index] ?? "";
    const showTooltip = tooltipIndex === index;
    return (
      <span
        className="info-icon-wrapper"
        ref={showInlineTooltip && showTooltip ? tooltipRef : null}
      >
        <span
          className="info-icon"
          onClick={(e) => { e.stopPropagation(); toggleTooltip(index); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleTooltip(index); }
          }}
          role="button"
          tabIndex={0}
          aria-label="Show clue"
          aria-expanded={showTooltip}
        >
          i
        </span>
        {showInlineTooltip && showTooltip && indication && (
          <span className="word-tooltip">
            <button
              type="button"
              className="word-tooltip-close"
              onClick={(e) => { e.stopPropagation(); setTooltipIndex(null); }}
              aria-label="Close hint"
            >
              &times;
            </button>
            <span className="word-tooltip-text">{indication}</span>
          </span>
        )}
      </span>
    );
  };

  const renderWordListItem = (word, index, { mobile = false } = {}) => {
    const isFound = foundWords.includes(word);
    const hintOpen = tooltipIndex === index;
    const btnClass = `word-list-btn${isFound ? " word-found" : ""}${hintOpen ? " word-list-btn--hint-open" : ""}`;
    const showInlineTooltip = !mobile;
    if (wordDisplayMode === "text") {
      return (
        <li key={index} className={btnClass}>
          <span className="word-indication-number">{index + 1}.</span>
          {renderInfoIcon(index, showInlineTooltip)}
        </li>
      );
    }
    const showMasked = wordDisplayMode === "masked" || wordDisplayMode === "maskedhint";
    return (
      <li key={index} className={btnClass}>
        <span className="word-indication-number">{index + 1}.</span>
        {wordDisplayMode === "normal" && word}
        {showMasked && (isFound ? word : maskWord(word))}
        {wordDisplayMode === "maskedhint" && <>{" "}{renderInfoIcon(index, showInlineTooltip)}</>}
      </li>
    );
  };

  const mobileHintText =
    tooltipIndex !== null ? (wordIndications[tooltipIndex] ?? "") : "";

  const wordListThemeStyle = {
    "--word-btn-fg": wordButtonTextColor,
    "--word-btn-bg": wordButtonBgColor,
    "--glass-tint": wordButtonBgColor,
  };
  const gridThemeStyle = {
    "--grid-cell-bg": wordGridBgColor,
    "--grid-cell-fg": wordGridTextColor,
    "--active-bg": gridColor,
    "--active-fg": gridActiveTextColor,
    "--cell-size-desktop": `${desktopCellSize}px`,
    "--cell-size-mobile": `${mobileCellSize}px`,
    "--cell-font-size-desktop": `${desktopFontSize}px`,
    "--cell-font-size-mobile": `${mobileFontSize}px`,
  };
  const progressFillStyle = {
    width: foundCount > 0 ? `${progressPercent}%` : "0%",
    "--progress-fill-bg": beginSubmitProgressBgColor,
    "--progress-text-color": textColor,
  };

  const renderProgressBar = () => (
    <div className="progress-wrapper game-progress-row">
      <div className="progress-bar">
        {foundCount > 0 ? (
          <div className="progress-fill" style={progressFillStyle}>
            <span className="progress-text">
              {foundCount} / {totalWords}
            </span>
          </div>
        ) : (
          <div className="progress-empty-text" style={{ color: titleTextColor }}>
            {foundCount} / {totalWords}
          </div>
        )}
      </div>
    </div>
  );

  const leftHintOpen = tooltipIndex !== null && tooltipIndex < leftWordCount;
  const rightHintOpen = tooltipIndex !== null && tooltipIndex >= leftWordCount;

  const gridPadDesktop = 20;
  const gridBorder = 2;
  const gridPadMobile = 16;
  const gridBoxWidthDesktop = desktopCellSize * gridSize + gridPadDesktop + gridBorder;
  const gridBoxWidthMobile = mobileCellSize * gridSize + gridPadMobile + gridBorder;
  const gameLayoutStyle = {
    "--game-grid-box-width": `${gridBoxWidthDesktop}px`,
    "--game-grid-box-width-mob": `${gridBoxWidthMobile}px`,
    "--word-btn-fg": wordButtonTextColor,
    "--word-btn-bg": wordButtonBgColor,
    "--glass-tint": wordButtonBgColor,
    "--bar-fg": btnTextColor,
    "--bar-bg": btnBgColor,
    "--submit-glass-tint": beginSubmitProgressBgColor,
  };

  return (
    <div className="game-wrapper" style={gameLayoutStyle}>
      <h1
        className="gameTile"
        style={{ "--game-title-color": titleTextColor }}
      >
        {gameTitleLines.map((line, i) => (
          <span key={i} className="gameTile-line">
            {line}
          </span>
        ))}
      </h1>

      <div
        className="game-play-area game-play-area--board"
        style={{ "--bar-fg": btnTextColor, "--bar-bg": btnBgColor }}
      >
        <div className="game-play-stack game-board-layout">
          <div className="game-header-top">
          <div className="game-bar-left">
            <button
          type="button"
          className="top-icon-btn rules-btn game-glass"
          onClick={() => setRulesOpen(true)}
          aria-label="Open rules"
        >
          <span
            className="top-icon-mask"
            style={{ "--icon-url": `url(${process.env.PUBLIC_URL}/img/rules-icon.png)` }}
            aria-hidden="true"
          />
        </button>
        <div className="top-stat timer game-glass" aria-label={`Time ${formatTime(time)}`}>
          <span
            className="top-icon-mask"
            style={{ "--icon-url": `url(${process.env.PUBLIC_URL}/img/timer-icon.png)` }}
            aria-hidden="true"
          />
          <span>{formatTime(time)}</span>
        </div>
          </div>
          <div className="top-stat points game-glass" aria-label={`Points ${score}`}>
            <span
              className="top-icon-mask"
              style={{ "--icon-url": `url(${process.env.PUBLIC_URL}/img/points-icon.png)` }}
              aria-hidden="true"
            />
            <span>{score}</span>
          </div>
        </div>
          {renderProgressBar()}
          <div className="game-main-row">
            <div
              className={`word-list word-list--side word-list--left${leftHintOpen ? " word-list--elevated" : ""}`}
              style={wordListThemeStyle}
            >
              <ul>
                {words.slice(0, leftWordCount).map((word, i) => renderWordListItem(word, i))}
              </ul>
            </div>

            <div className="game-grid-column">
              <div className="puzzle-container" style={gridThemeStyle}>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}>
              {grid.map((row, r) =>
                row.map((letter, c) => {
                  const selectedNow = selected.some((p) => p[0] === r && p[1] === c);
                  const found = foundCells.some((p) => p[0] === r && p[1] === c);
                  return (
                    <div
                      key={`${r}-${c}`}
                      data-pos={`${r},${c}`}
                      className={`cell ${selectedNow ? "active" : ""} ${found ? "found" : ""}`}
                      onMouseDown={() => start(r, c)}
                      onMouseEnter={() => move(r, c)}
                      onMouseUp={end}
                      onTouchStart={() => start(r, c)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={end}
                    >
                      {letter}
                    </div>
                  );
                })
              )}
              </div>
              </div>
            </div>

            {words.length > leftWordCount && (
              <div
                className={`word-list word-list--side word-list--right${rightHintOpen ? " word-list--elevated" : ""}`}
                style={wordListThemeStyle}
              >
                <ul>
                  {words.slice(leftWordCount).map((word, i) => renderWordListItem(word, leftWordCount + i))}
                </ul>
              </div>
            )}
          </div>

          <input
            type="button"
            value="Submit"
            onClick={handleSubmit}
            disabled={!canShowSubmit}
            className="btnSubmit game-glass"
            style={{
              color: textColor,
              "--submit-bg": beginSubmitProgressBgColor,
              cursor: canShowSubmit ? "pointer" : "not-allowed",
              opacity: canShowSubmit ? 1 : 0.6,
            }}
          />

          <div className="word-list word-list-mobile" style={wordListThemeStyle}>
            {tooltipIndex !== null && mobileHintText && (
              <div
                className="word-list-mobile-hint-panel"
                ref={mobileHintRef}
                role="region"
                aria-label={`Hint for word ${tooltipIndex + 1}`}
              >
                <button
                  type="button"
                  className="word-tooltip-close"
                  onClick={() => setTooltipIndex(null)}
                  aria-label="Close hint"
                >
                  &times;
                </button>
                <p className="word-list-mobile-hint-text">{mobileHintText}</p>
              </div>
            )}
            <ul>
              {words.map((word, index) => renderWordListItem(word, index, { mobile: true }))}
            </ul>
          </div>
        </div>
      </div>

      {rulesOpen && (
        <div
          className="game-rules-overlay"
          role="presentation"
          onClick={() => setRulesOpen(false)}
        >
          <div
            className="game-rules-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="game-rules-title"
            style={{
              "--rules-popup-bg": wordGridBgColor,
              "--rules-popup-fg": wordGridTextColor,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="game-rules-modal-close"
              onClick={() => setRulesOpen(false)}
              aria-label="Close rules"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <h2 id="game-rules-title" className="game-rules-modal-title">
              How to play
            </h2>
            <div className="game-rules-modal-body">
              {rulesList.length ? (
                <ol className="game-rules-list">
                  {rulesList.map((rule, i) => (
                    <li key={i}>{rule}</li>
                  ))}
                </ol>
              ) : (
                <p>No rules available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
